import { AuthStatus } from '@amzn/bf-playback'
import { createLogPrefix, logDebug } from '@amazon/vinyl-util'
import { handleError } from '@/errorHandler'
import type { AuthTokenProvider, AuthTokenWithRefresh } from '@amzn/vinyl-amzn'
import {
    exchangeCodeForToken,
    oAuthAuthorize,
    type OAuthOptions,
    refreshToken,
} from '@/auth/oAuth2'
import { localSignInPersistence } from '@/core/signInPersistence'
import { decrypt } from '@/util/crypto'
import { TokenAuth } from '@/auth/TokenAuth'
import { IS_TAURI } from '@/util/platform'

// Under Tauri the app origin (`app://localhost` or `tauri://localhost`) is
// not on LWA's allow-list, so the OAuth redirect goes to the hosted
// login.html. The Tauri-owned popup catches the redirect navigation in Rust
// and pulls the `code` out before login.html has a chance to render.
const redirectUri = IS_TAURI
    ? 'https://amp.music.amazon.dev/login.html'
    : new URL('login.html', location.origin).href

const OAUTH_CONFIG = {
    // Using DragonFly Web's security profile
    clientId: 'amzn1.application-oa2-client.339f46ec317249bd8507340b1ec9c7d7',
    authorizationEndpoint: 'https://www.amazon.com/ap/oa',
    tokenEndpoint: 'https://api.amazon.com/auth/o2/token',
    scopes: ['profile'],
    clientSecret: decrypt(
        // Temporary: will switch to AuthPortal
        'ZTpkfbm5UZ4oXORtsCoq2Ywm+5Il1I9o9U9PaePB6XLvO8ydcGiKlZXCPFXM/PUHTLjpd8KdVORLyiobP1rf/Y1zBA9Y9rXzziztgDHz4JD7O8+W06Mt++UwGGn+AcDCbN7AdSgTgpMASmIz',
        'bullfrogInsecurePw'
    ),
    redirectUri,
} as const satisfies OAuthOptions

export class LwaAuth extends TokenAuth {
    logPrefix = createLogPrefix('LwaAuth')

    constructor() {
        super(AuthStatus.PENDING)
        localSignInPersistence.get().then(this.setToken).catch(this.errorHandler)
    }

    signIn(): void {
        logDebug(this, 'signIn')
        oAuthAuthorize(OAUTH_CONFIG)
            .then((code) => {
                // Never log the code itself — it exchanges for an access token.
                logDebug(this, 'authorization code retrieved')
                exchangeCodeForToken(OAUTH_CONFIG, code)
                    .then(this.setToken)
                    .catch(this.errorHandler)
            })
            .catch(this.errorHandler)
    }

    signOut(): void {
        logDebug(this, 'signOut')
        // TODO: revoke token
        this.setToken(null)
    }

    private reportError = (error: unknown) => handleError(this, error)

    private errorHandler = (error: unknown) => {
        this.reportError(error)
        this.signOut()
    }

    private setToken = (token: AuthTokenWithRefresh | null) => {
        if (token) {
            const provider = createAuthTokenProvider(token, (newToken) => {
                // Only the currently-active provider may persist. A refresh
                // that resolves after sign-out (provider nulled) or after a
                // different account signed in (provider replaced) must NOT
                // re-write the old token into localStorage — that is the exact
                // account bleed the sign-out teardown exists to prevent.
                if (this._authTokenProvider !== provider) return
                logDebug(this, 'token refreshed')
                localSignInPersistence.set(newToken).catch(this.errorHandler)
            })
            this._authTokenProvider = provider
            localSignInPersistence.set(token).catch(this.errorHandler)
            this.setStatus(AuthStatus.SIGNED_IN)
        } else {
            // Report-only: `errorHandler` signs out, which re-enters this
            // branch and calls `remove()` again — a self-feeding microtask
            // chain that starves the main thread if removal keeps failing.
            localSignInPersistence.remove().catch(this.reportError)
            this._authTokenProvider = null
            this.setStatus(AuthStatus.SIGNED_OUT)
        }
    }
}

function createAuthTokenProvider(
    token: AuthTokenWithRefresh,
    onRefreshed?: (token: AuthTokenWithRefresh) => void
): AuthTokenProvider {
    let authTokenRefreshPromise: Promise<AuthTokenWithRefresh> | null = null
    return async () => {
        if (token.expiresAt > Date.now() + 60 * 5 * 1000) {
            // Does not expire in the next 5 minutes, return the current token.
            return token.accessToken
        } else {
            if (!authTokenRefreshPromise) {
                authTokenRefreshPromise = refreshToken(OAUTH_CONFIG, token.refreshToken).finally(
                    () => (authTokenRefreshPromise = null)
                )
            }
            token = await authTokenRefreshPromise
            onRefreshed?.(token)
            return token.accessToken
        }
    }
}
